import { productMetaFieldTransformer } from '@/components/shop/products/Product';
import { useCartStore } from '@/stores/cart';

/**
 * Fetches the add-on product if the primary product has a digitalAddOnHandle metafield.
 * @param {Object} product - The transformed product object
 * @param {Function} fetcher - Function to fetch product by handle (must be captured synchronously in setup/asyncData)
 * @returns {Promise<Object|null>} The transformed add-on product or null
 */
export async function fetchAddOnProduct(product, fetcher) {
  const handle = product?.digitalAddOn?.digitalAddOnHandle;
  if (!handle) return null;

  try {
    const addOnRequest = await fetcher(handle);
    return addOnRequest ? productMetaFieldTransformer(addOnRequest) : null;
  } catch (error) {
    console.error('Error fetching add-on product:', error);
    return null;
  }
}

export const addVariantToCart = async function (
  product,
  currentVariant,
  isCourse = false,
  rating = null,
  addOn = null,
  quantity = 1
) {
  if (product.handle) {
    this.$dlPush.eECaddToCart(currentVariant, product, quantity, rating);
  }

  const cartStore = useCartStore();

  const currentLineItem = cartStore.lineItems
    ? cartStore.lineItems.find((lineItem) => {
        if (currentVariant.id.startsWith('gid://shopify/ProductVariant')) {
          return lineItem.variant?.id === currentVariant.id;
        } else {
          try {
            return lineItem.variant?.id === atob(currentVariant.id);
          } catch (e) {
            return false;
          }
        }
      })
    : null;

  // check if we need to discard this because max quantity is reached
  if (currentLineItem) {
    const maxQuantity =
      Number(
        currentLineItem.attributes.find((attr) => {
          return attr.key === '_maxQuantity';
        })?.value
      ) || false;

    if (maxQuantity && currentLineItem.quantity >= maxQuantity) {
      // open cart and quit early
      cartStore.open();
      return;
    }
  }

  const basket = [
    {
      merchandiseId: currentVariant.id,
      quantity: quantity,
      attributes: [
        { key: '_maxQuantity', value: isCourse ? '1' : '0' },
        { key: '_isCourse', value: isCourse ? 'true' : 'false' },
      ],
    },
  ];

  if (addOn) {
    const variant = addOn.variants?.edges[0]?.node?.id;

    // need to check if add on is already in cart
    const addOnInCart =
      cartStore.lineItems?.findIndex((lineItem) => {
        return lineItem.variant.id === variant;
      }) !== -1;

    if (!addOnInCart) {
      const IS_ADDON_KEY = '_isAddOn';

      basket.push({
        merchandiseId: variant,
        quantity: 1,
        attributes: [
          { key: IS_ADDON_KEY, value: 'true' },
          { key: '_bundledBy', value: product.handle },
          { key: '_cartImage', value: product.digitalAddOn?.digitalAddOnMainImageFullPath },
          { key: '_cartText', value: product.digitalAddOn?.digitalAddOnName },
          { key: '_maxQuantity', value: '1' },
        ],
      });
    }
  }

  await cartStore.addLineItems(basket);

  cartStore.open();
};

/**
 * Parse add-to-cart query parameters from the current URL.
 * Returns a normalized instruction object that components can act on directly.
 *
 * Formats supported:
 * - ?atc=2                   => { target: 'pdp', action: 'firstVariant', quantity: 2 }
 * - ?atc=A003851,2           => { target: 'pdp', action: 'selectedVariant', sku: 'A003851', quantity: 2 }
 * - ?atc=xsell,3             => { target: 'xsell', action: 'bundle', quantity: 3 }
 * - ?sku=A003851&atc=1       => { target: 'global', action: 'addBySku', sku: 'A003851', quantity: 1 }
 */
export function getAddToCartParamsFromUrl() {
  if (typeof window === 'undefined') return null;

  const searchParams = new URLSearchParams(window.location.search || '');
  const atcParam = searchParams.get('atc');
  if (!atcParam) return null;

  const [type, quantityStr] = atcParam.toString().split(',');
  const quantity = quantityStr ? parseInt(quantityStr, 10) || 1 : 1;

  // Check for separate sku parameter: ?sku=A003851&atc=1
  const skuParam = searchParams.get('sku');
  if (skuParam) {
    const quantityFromAtc = parseInt(atcParam, 10) || 1;
    return { target: 'global', action: 'addBySku', sku: skuParam.toUpperCase(), quantity: quantityFromAtc };
  }

  // xsell path
  if (atcParam.includes(',')) {
    if (type === 'xsell') {
      return { target: 'xsell', action: 'bundle', quantity };
    }
    // sku,quantity
    const sku = type;
    if (sku.startsWith('A')) {
      return { target: 'pdp', action: 'selectedVariant', sku, quantity };
    }
    return null;
  }

  // first variant with quantity
  const quantityNum = parseInt(type, 10);
  if (!isNaN(quantityNum)) {
    return { target: 'pdp', action: 'firstVariant', quantity: quantityNum };
  }

  return null;
}
